// VGM encoder — the inverse of ./vgm.ts. Turns a frame-stamped list of
// sound-chip register writes (captured from a running game, or authored)
// into a .vgm byte blob. Timing is frame-quantized: every write carries
// the frame it happened on, and the encoder emits a one-frame wait (0x62
// = 735 samples, the NTSC 60 Hz frame) between consecutive frames. This
// matches how these chips are actually driven — a game writes its sound
// registers once per NMI — and round-trips cleanly through parseVgm.
const DEFAULT_CLOCK = {
    "nes-2a03": 1789773,
    "gb-dmg": 4194304,
};
const WRITE_CMD = {
    "nes-2a03": 0xb4,
    "gb-dmg": 0xb3,
};
// Header offsets (must match ./vgm.ts).
const HEADER_LEN = 0x100;
export function encodeVgm(opts) {
    const { chip, events } = opts;
    const clock = opts.clock ?? DEFAULT_CLOCK[chip];
    const writeCmd = WRITE_CMD[chip];
    // Group writes by frame, preserving in-frame order.
    const sorted = [...events].sort((a, b) => a.frame - b.frame);
    const lastFrame = sorted.length > 0 ? sorted[sorted.length - 1].frame : -1;
    const totalFrames = Math.max(opts.totalFrames ?? lastFrame + 1, lastFrame + 1);
    const data = [];
    let loopByteOffset = -1; // absolute byte offset of the loop point in data
    let ei = 0;
    for (let frame = 0; frame < totalFrames; frame++) {
        if (frame === opts.loopFrame)
            loopByteOffset = HEADER_LEN + data.length;
        while (ei < sorted.length && sorted[ei].frame === frame) {
            const e = sorted[ei];
            data.push(writeCmd, e.reg & 0xff, e.value & 0xff);
            ei++;
        }
        // One 60 Hz frame of wait, except after the final frame (the end
        // marker follows immediately).
        if (frame < totalFrames - 1)
            data.push(0x62);
    }
    data.push(0x66); // end of stream
    const totalSamples = totalFrames > 0 ? (totalFrames - 1) * 735 : 0;
    const out = new Uint8Array(HEADER_LEN + data.length);
    const dv = new DataView(out.buffer);
    dv.setUint32(0x00, 0x206d6756, true); // "Vgm "
    dv.setUint32(0x04, out.length - 0x04, true); // EOF offset
    dv.setUint32(0x08, 0x161, true); // version 1.61
    dv.setUint32(0x18, totalSamples, true); // total samples
    if (loopByteOffset >= 0) {
        dv.setUint32(0x1c, loopByteOffset - 0x1c, true); // loop offset
        // Waits replayed from loopFrame to the end number (totalFrames-1)-loopFrame,
        // mirroring the total-samples count above (no wait after the final frame).
        dv.setUint32(0x20, (totalFrames - 1 - opts.loopFrame) * 735, true); // loop samples
    }
    dv.setUint32(0x34, HEADER_LEN - 0x34, true); // data starts at 0x100
    if (chip === "nes-2a03")
        dv.setUint32(0x84, clock, true);
    else
        dv.setUint32(0x80, clock, true);
    out.set(data, HEADER_LEN);
    return out;
}
// Trim leading (and optionally trailing) silent frames out of a capture's
// event list, given the active span [first, last] (see
// measureActiveFrameSpan). Leading writes are PRESERVED, just collapsed
// onto frame 0 — a sound's pre-trigger setup (duty, sweep) happens during
// the silent lead and must survive, so we keep every write and only remove
// the silent *duration*. Trailing frames past `last` are dropped only when
// `trimTrailing` (one-shot SFX); a loop keeps them (its final rest may be
// intentional — docs/sound.md §"Auto-trim").
export function trimVgmEvents(events, frameCount, span, trimTrailing) {
    const { first, last } = span;
    const out = [];
    for (const e of events) {
        if (trimTrailing && e.frame > last)
            continue;
        out.push({ frame: Math.max(0, e.frame - first), reg: e.reg, value: e.value });
    }
    const endFrame = trimTrailing ? last : Math.max(0, frameCount - 1);
    const totalFrames = Math.max(1, endFrame - first + 1);
    return { events: out, totalFrames };
}
//# sourceMappingURL=vgm-encode.js.map